import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";

import {
  TableSchema,
  TableTypes,
  DataTableComponentBase,
  DataTableComponent,
  TableService,
  Filter,
} from "@kortobaa-front/k-ng-datatable";

import { AuthService } from "src/app/core/auth.service";
import { DataService } from "src/app/shared/services/data.service";
import { ConfirmDialogComponent } from "src/app/shared/components/confirm-dialog/confirm-dialog.component";
import { getHeadersWithToken } from "src/app/shared/helpers/getHeadersWithToken";
import { environment } from "src/environments/environment";
import * as moment from "moment-timezone";
import { isIsoDate } from "src/app/shared/helpers/date.helper";
import { ViewEvaluationDialogComponent } from "../view-evaluation-dialog/view-evaluation-dialog.component";
import { ActivatedRoute, Router } from "@angular/router";
import { FilterUsersDialogComponent } from "src/app/users/dialogs/filter-users-dialog/filter-users-dialog.component";
import { Subscription } from "rxjs";
import { ServicesSlug } from "src/app/shared/models/interfaces";

@Component({
  selector: "app-evaluations-report",
  templateUrl: "./evaluations-report.component.html",
  styleUrls: ["./evaluations-report.component.scss"],
})
export class EvaluationsReportComponent
  extends DataTableComponentBase
  implements OnInit, OnDestroy
{
  moduleName = "reports";
  url = "evaluations-cases-reports";
  filters: Filter = {
    limit: 10,
    offset: 0,
    order: ["id DESC"],
    where: {
      and: [],
    },
  };

  displayedColumns: TableSchema[] = [
    {
      title: "case-num",
      id: "application_no",
      type: TableTypes.text,
      editable: false,
      notOnFilter: true,
    },
    {
      title: "case-title",
      id: "title",
      type: TableTypes.text,
      editable: false,
      notOnFilter: false,
    },
    {
      title: "user-name",
      id: "user_name",
      type: TableTypes.text,
      editable: false,
      notOnFilter: true,
    },
    {
      title: "lawyerName",
      id: "lawyer_name",
      type: TableTypes.text,
      editable: false,
      notOnFilter: true,
    },
    {
      title: "conciliatorName",
      id: "conciliator_name",
      type: TableTypes.text,
      editable: false,
      notOnFilter: true,
    },
    {
      title: "evaluation-date",
      id: "created_at",
      type: TableTypes.text,
      editable: false,
      notOnFilter: true,
    },
    {
      title: "satisfactionPercent",
      id: "avg_satisfaction_percentage",
      type: TableTypes.text,
      editable: false,
      notOnFilter: true,
    },
    {
      title: "case-actions",
      id: "_",
      type: TableTypes.select,
      isActionsList: true,
      list: [
        { title: "chooses-action", value: "_" },
        { title: "view-evaluation", value: "view-evaluation" },
      ],
      editable: false,
      notOnFilter: true,
    },
  ];
  dataSource;
  isDataLoading = false;
  searchKeyword: string = "";

  dialogSub$!: Subscription;
  filterUsers!: any;
  filterUsersObj: any = {};
  @ViewChild(DataTableComponent)
  private _dataTableComponent: DataTableComponent;
  serviceSlug!: string;
  dataTableTitle!: string;
  constructor(
    public authService: AuthService,
    private _tableService: TableService,
    private _dataService: DataService,
    private _dialog: MatDialog,
    private _http: HttpClient,
    private _translate: TranslateService,
    private _router: Router,
    private _activeRoute: ActivatedRoute
  ) {
    super();
    this._tableService.tableUrl = "admin/evaluations-cases-reports";

    this._activeRoute.paramMap.subscribe(params => {
      this.serviceSlug = params.get('serviceSlug');
      this.dataTableTitle =
        this.serviceSlug == ServicesSlug.INQUIRY_SERVICES
          ? "evaluations-inquiries-reports"
          : this.serviceSlug == ServicesSlug.COURT_SERVICES
          ? "evaluations-cases-reports"
          : "evaluations-writing-cases-reports";
      if(this.dataSource){
        this._router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
          this._router.navigate([`evaluations-cases-reports/${this.serviceSlug}`]);
        });
      }
    });
  }

  async ngOnInit() {}

  async getData(filters?: Filter) {
    if (
      !filters.where.and ||
      filters.where.and.length === 0 ||
      filters.where.and.every((obj) => Object.keys(obj).length === 0)
    ) {
      filters.where.and = [{ service: this.serviceSlug }];
    } else {
      // Remove any existing object containing a 'service' property
      filters.where.and = filters.where.and.filter(
        (obj) => !obj.hasOwnProperty("service")
      );
      // Add the new service object
      filters.where.and.unshift({ service: this.serviceSlug });
    }
    
    this.isDataLoading = true;
    try {
      this._tableService.tableUrl = "admin/evaluations";
      let data = await this._tableService.getData<any>(filters).toPromise();
      this.dataSource = data.map((elem) => {
        let lawyerName = "-";
        let ConciliatorName = "-";
        if (elem.assignee_role == 5) {
          lawyerName = elem?.assignee_name;
        }
        if (elem.assignee_role == 6) {
          ConciliatorName = elem?.assignee_name;
        }

        const isDate = isIsoDate(elem["created_at"]);
        const isNullish = elem["created_at"] == null;
        if (isNullish) elem["created_at"] = "غير متاح";
        if (isDate) {
          moment.locale("ar");
          elem["created_at"] = moment(new Date(elem["created_at"])).format(
            "LL"
          );
        }
        return {
          ...elem,
          conciliator_name: ConciliatorName,
          lawyer_name: lawyerName,
          avg_satisfaction_percentage: "%" + elem.avg_satisfaction_percentage.slice(0, 5),
        };
      });
    } catch (err) {
      // console.error(err);
    }

    this.isDataLoading = false;
  }

  async getExportData(data) {
    try {
      this._tableService.tableUrl = "admin/evaluations";
      let filters = { where: {...this._dataTableComponent.backendFilter.where} };
      let data = await this._tableService.getData<any>(filters).toPromise();
      data = data.map((elem) => {
        let lawyerName = "-";
        let ConciliatorName = "-";
        if (elem.assignee_role == 5) {
          lawyerName = elem?.assignee_name;
        }
        if (elem.assignee_role == 6) {
          ConciliatorName = elem?.assignee_name;
        }

        const isDate = isIsoDate(elem["created_at"]);
        const isNullish = elem["created_at"] == null;
        if (isNullish) elem["created_at"] = "غير متاح";
        if (isDate) {
          moment.locale("ar");
          elem["created_at"] = moment(new Date(elem["created_at"])).format(
            "LL"
          );
        }
        return {
          ...elem,
          conciliator_name: ConciliatorName,
          lawyer_name: lawyerName,
          avg_satisfaction_percentage: "%" + elem.avg_satisfaction_percentage.slice(0, 5),
        };
      });
      await this._dataTableComponent.downloadAllData(data);
    } catch (err) {
      // console.error(err);
    }
  }

  isIsoDate(arg0: any) {
    throw new Error("Method not implemented.");
  }

  getDataForAction(data) {
    this._tableService.SaveItem(data).subscribe(
      (row: any) => {
        this._tableService.redirect(this.url);
      },
      (error) => {
        this._dataService.notification(error.error.error.message);
      }
    );
  }

  async handleRowActions(data: { action: string; row: any }) {
    const { action, row } = data;
    const evaluationId = row.id;

    if (action == "view-evaluation") {
      await this.handleViewEvaluationDialog(evaluationId);
    }
  }

  async handleViewEvaluationDialog(evaluationId: Number) {
    const dialogData: null | { assigneeIds: number[] } = await this._dialog
      .open(ViewEvaluationDialogComponent, {
        direction: this._translate.currentLang == "ar" ? "rtl" : "ltr",
        panelClass: "white-dialog",
        width: "80rem",
        minHeight: "40rem",
        maxWidth: "90vw",
        autoFocus: false,
        data: {
          evaluationId,
          status: this.serviceSlug === "khdmat_alastsharh_alqanwnyh"? "inquiry": "case",
        },
      })
      .afterClosed()
      .toPromise();
  }

  async handleFinishCaseDialog(caseId: number) {
    const isFinish: boolean = await this._dialog
      .open(ConfirmDialogComponent, {
        direction: this._translate.currentLang == "ar" ? "rtl" : "ltr",
        panelClass: "confirm-dialog",
        data: {
          content: this._translate.instant(
            "confirm-dialog.are-you-want-finish-case"
          ),
          icon: "close",
        },
      })
      .afterClosed()
      .toPromise();

    if (isFinish) {
      try {
        await this._http
          .patch(
            `${environment.apiHost}admin/response-user-cases/${caseId}`,
            {
              status: "FINISH",
            },
            {
              headers: getHeadersWithToken(),
            }
          )
          .toPromise();

        this._tableService.redirect(this.url);
      } catch (err) {
        // console.error(err);
      }
    }
  }

  async handleSearchChange(value: string) {
    this.isDataLoading = true;
    if (!value) this._dataTableComponent.paginator.firstPage();
    this.searchKeyword = value;
    this.filters.where.and = this.filters.where.and.filter(
      (item) => !item.keyword
    );
    if (value.length > 0) {
      this.filters.where.and.push({ keyword: value });
    } else {
      if (this.filters.where.and.length === 0) {
        this._router
          .navigateByUrl("/", { skipLocationChange: true })
          .then(() => {
            this._router.navigate([`evaluations-cases-reports/${this.serviceSlug}`]);
          });
      }
    }

    this._dataTableComponent.backendFilter = this.filters;

    this.getData(this._dataTableComponent.backendFilter);

    this._dataTableComponent.tableDataLength = (
      await this._tableService
        .getDataCount(this._dataTableComponent.backendFilter.where)
        .toPromise()
    ).count;

    this.isDataLoading = false;
  }

  async filterByDate(value: any) {
    let startDate = moment(value.startDate).add(3, "h").toDate()?.toISOString();
    let endDate = moment(value.endDate).add(3, "h").toDate()?.toISOString();
    this.filters["where"] = {
      and: [{ created_at: { gt: startDate } }, { created_at: { lt: endDate } }],
    };
    this.getData(this.filters);
  }

  showUserData({ data }) {
    this.handleCustomFilter("customFilter");
  }
  // handle filter dialog
  async handleCustomFilter(data: any) {
    if (data == "customFilter") {
      this.dialogSub$ = this._dialog
        .open(FilterUsersDialogComponent, {
          direction: "rtl",
          panelClass: "save-case-dialog",
          data: {
            displayData: this.displayedColumns,
            list: "user-complaint",
          },
        })
        .afterClosed()
        .subscribe(async (filterUsers: any) => {
          if (filterUsers) {
            this.isDataLoading = true;
            this.handleFilter(filterUsers);
          }
        });
    }
  }

  // handle filteration
  async handleFilter(obj: any) {
    this.filterUsers = obj;
    let body: any = { and: [] };
    if (obj.dateToValue && obj.dateFromValue) {
      body.and = [
        { created_at: { lt: obj.dateToValue } },
        { created_at: { gt: obj.dateFromValue } },
      ];
    }

    if (this.searchKeyword.length > 0) {
      body.and.push({ keyword: this.searchKeyword });
    }

    for (let item of body.and) {
      if (item.created_at) {
        if (item.created_at.lt) {
          this.filterUsersObj.dateToValue = item.created_at.lt;
        }
        if (item.created_at.gt) {
          this.filterUsersObj.dateFromValue = item.created_at.gt;
        }
      }
    }

    this.filters["where"] = {
      ...this.filters["where"],
      ...body,
    };

    this._dataTableComponent.backendFilter = this.filters;
    this.getData(this.filters);
    this._dataTableComponent.tableDataLength = (
      await this._tableService.getDataCount(this.filters["where"]).toPromise()
    ).count;
    this.isDataLoading = false;
  }

  //remove filter - type and update filter and display it from api
  removeFilter(value: any) {
    if (value == "dateFromValue" || value == "dateToValue") {
      delete this.filterUsersObj["dateFromValue"];
      delete this.filterUsersObj["dateToValue"];
      delete this.filterUsers["dateFromValue"];
      delete this.filterUsers["dateToValue"];
    } else {
      delete this.filterUsersObj[value];
      delete this.filterUsers[value];
    }
    if (Object.keys(this.filterUsersObj).length === 0) {
      this._router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
        this._router.navigate([`evaluations-cases-reports/${this.serviceSlug}`]);
      });
    } else {
      this.handleFilter(this.filterUsers);
    }
  }

  ngOnDestroy(): void {
    this.dialogSub$?.unsubscribe();
  }
}
